/*
 * Where: src/constants/citizenSupportByCardUid.js
 * What: Card UID to citizen support mapping in JSON-like object format.
 * Why: Prepare task 3 data source for support candidate lookup.
 */
const createSupport = (support_id, support_name, support_content) => ({
    support_id,
    support_name,
    support_content,
});

const SUPPORTS_FOR_HANAKO = [
    createSupport(
        '10000',
        '住民税非課税世帯向け給付（10万円）',
        '住民税非課税世帯を対象に、生活費負担の軽減を目的として1世帯あたり10万円を支給する制度です。申請時には本人確認書類、世帯情報、振込先口座の確認書類が必要です。'
    ),
    createSupport(
        '10239',
        '子育て世帯生活支援特別給付（5万円）',
        '18歳年度末までの子どもを養育する世帯向けに、食費・教育費上昇への対応として5万円を給付する制度です。所得要件や扶養状況により対象判定が行われます。'
    ),
    createSupport(
        '10888',
        '省エネ家電買替補助（上限3万円）',
        '一定の省エネ基準を満たす冷蔵庫・エアコンなどへ買い替えた場合に、購入費の一部を補助する制度です。対象機種の型番、購入日、領収書の提出が必要です。'
    ),
    createSupport(
        '11001',
        '在宅ワーク環境整備補助（上限2万円）',
        '在宅就労に必要な机、椅子、通信機器の購入費を補助する制度です。就労実態の確認書類と購入証明の提出が必要です。'
    ),
    createSupport(
        '11002',
        '学習端末購入補助（上限4万円）',
        '中高生の学習機会確保を目的に、指定要件を満たす学習端末の購入費を補助する制度です。対象児童の在籍確認が必要です。'
    ),
    createSupport(
        '11003',
        '電力価格高騰対策給付（3万円）',
        '電気料金の負担が大きい世帯に対し、年度内1回に限り3万円を給付する制度です。世帯所得と居住実態で対象判定を行います。'
    ),
    createSupport(
        '11004',
        '妊産婦健診交通費補助（上限1.5万円）',
        '妊産婦が健診を受ける際の交通費を補助する制度です。健診受診記録と交通費領収書の提出により精算されます。'
    ),
    createSupport(
        '11005',
        '防災用品購入補助（上限1万円）',
        '非常食、簡易トイレ、蓄電池などの防災用品購入費を補助する制度です。対象品目の購入証明が必要です。'
    ),
    createSupport(
        '11006',
        '保育料軽減補助（条件付き）',
        '保育施設利用世帯の負担軽減を目的に、所得区分に応じて保育料の一部を補助する制度です。住民税情報に基づき判定されます。'
    ),
    createSupport(
        '11007',
        '住宅断熱改修補助（上限15万円）',
        '断熱窓や断熱材施工など、住宅の省エネ改修費を補助する制度です。事前申請と施工内容の審査が必要です。'
    ),
    createSupport(
        '11008',
        '多子世帯食費支援給付（年額4万円）',
        '子ども3人以上を養育する世帯へ、食費負担軽減のため年額4万円を給付する制度です。扶養人数と同居状況を確認します。'
    ),
    createSupport(
        '11009',
        '地域通貨チャージ支援（1万円分）',
        '地域通貨アプリへのチャージ額を上乗せして支援する制度です。対象期間内の本人チャージ利用が要件です。'
    ),
    createSupport(
        '11010',
        '引越し初期費用補助（上限5万円）',
        '就労や育児事情に伴う転居時の初期費用を補助する制度です。賃貸契約書、見積書、領収書が必要です。'
    ),
    createSupport(
        '11011',
        'ひとり親学習支援クーポン（年額3万円）',
        'ひとり親世帯の子ども向けに、学習塾や教材購入へ使える年額3万円相当のクーポンを交付する制度です。'
    ),
    createSupport(
        '11012',
        '子ども医療費自己負担助成（条件付き）',
        '子どもの通院・入院にかかる自己負担額を助成する制度です。年齢区分と所得制限に応じて助成上限が異なります。'
    ),
];

const SUPPORTS_FOR_TARO = [
    createSupport(
        '20010',
        '高齢者向け交通費助成（年額2万円）',
        '高齢者の外出機会確保を目的として、公共交通やタクシー利用に使える年額2万円分の助成を行う制度です。利用方法は自治体発行のICカードまたは利用券方式です。'
    ),
    createSupport(
        '20420',
        '住宅改修補助（上限20万円）',
        '手すり設置、段差解消、滑り防止床材への変更など、在宅生活の安全確保に必要な住宅改修費を補助する制度です。事前申請と工事見積書の提出が必要で、上限20万円まで対象となります。'
    ),
    createSupport(
        '20999',
        '見守りサービス利用補助（月額1,500円）',
        '一人暮らし高齢者などを対象に、緊急通報機器や定期安否確認サービスの利用料を月額1,500円まで補助する制度です。対象サービス事業者との契約証明が必要です。'
    ),
    createSupport(
        '21001',
        '高齢者デジタル相談利用補助（年額8千円）',
        'スマートフォン操作支援やオンライン手続き講習の利用料を補助する制度です。指定相談窓口の利用記録が必要です。'
    ),
    createSupport(
        '21002',
        '配食サービス利用補助（月額3千円）',
        '栄養確保と安否確認を兼ねた配食サービスの利用料を補助する制度です。介護認定または見守り対象者が主な対象です。'
    ),
    createSupport(
        '21003',
        '補聴器購入費助成（上限5万円）',
        '聴力低下による日常生活支援のため、補聴器購入費の一部を助成する制度です。医師意見書と見積書が必要です。'
    ),
    createSupport(
        '21004',
        'フレイル予防教室参加補助（年額1万円）',
        '運動・栄養指導などのフレイル予防教室への参加費を補助する制度です。出席実績に応じて補助額が確定します。'
    ),
    createSupport(
        '21005',
        '家庭内手すり設置支援（上限6万円）',
        '転倒予防を目的とした手すり設置や小規模改修費を助成する制度です。住居現況確認後に支給決定されます。'
    ),
    createSupport(
        '21006',
        '通院付き添い支援券（年額1.2万円）',
        '通院時の付き添いサポートを利用する際に使える支援券を交付する制度です。対象医療機関への通院実績が必要です。'
    ),
    createSupport(
        '21007',
        '防犯機器設置補助（上限2万円）',
        '高齢者宅の防犯対策として、センサーライトや通報機器設置費を補助する制度です。設置完了後の写真提出が必要です。'
    ),
    createSupport(
        '21008',
        '介護用品購入補助（年額3万円）',
        '紙おむつや防水シーツなど介護用品の購入費を補助する制度です。対象品目と購入店舗に条件があります。'
    ),
    createSupport(
        '21009',
        '居宅清掃支援補助（年2回）',
        '高齢者世帯の住環境維持を目的に、指定事業者による居宅清掃費を補助する制度です。利用回数は年2回までです。'
    ),
    createSupport(
        '21010',
        '熱中症対策エアコン更新補助（上限7万円）',
        '高齢者宅の熱中症予防として、旧式エアコンから省エネ機器への更新費を補助する制度です。設置証明が必要です。'
    ),
    createSupport(
        '21011',
        '緊急通報端末設置補助（初期費用）',
        '緊急時に通報センターへ接続できる端末の初期導入費を補助する制度です。利用契約締結後に申請可能です。'
    ),
    createSupport(
        '21012',
        '高齢者タクシー利用券追加交付（条件付き）',
        '通院頻度が高い対象者向けに、通常枠を超えるタクシー利用券を追加交付する制度です。医療機関の通院証明が必要です。'
    ),
];

const SUPPORTS_FOR_ICHIRO = [
    createSupport(
        '30001',
        '就業支援講座受講費補助（上限8万円）',
        '再就職・転職に必要な資格取得や職業訓練講座の受講費を補助する制度です。指定講座に限り、受講修了確認後に上限8万円まで補助されます。'
    ),
    createSupport(
        '30555',
        '医療費自己負担軽減助成（条件付き）',
        '慢性疾患や継続治療が必要な世帯の医療費負担を軽減するため、自己負担額の一部を助成する制度です。世帯所得、診療明細、保険適用範囲などの条件確認があります。'
    ),
    createSupport(
        '30990',
        'ひとり親世帯家賃補助（月額2万円）',
        'ひとり親世帯の居住安定を目的に、民間賃貸住宅の家賃の一部を月額2万円まで補助する制度です。賃貸借契約書、収入状況、同居家族情報の提出が必要です。'
    ),
    createSupport(
        '31001',
        '職業訓練中生活支援給付（月額10万円）',
        '指定職業訓練の受講期間中に、生活費負担を軽減するため月額給付を行う制度です。訓練出席率と求職活動実績が要件です。'
    ),
    createSupport(
        '31002',
        '再就職準備金貸付（無利子・条件付き免除）',
        '再就職に必要な移動費や備品購入費を無利子で貸し付ける制度です。一定期間の就労継続で返済免除条件があります。'
    ),
    createSupport(
        '31003',
        '資格試験受験料補助（年2回まで）',
        '就職に有利な国家資格・公的資格の受験料を補助する制度です。合否に関わらず受験実績で補助対象となります。'
    ),
    createSupport(
        '31004',
        '通信費軽減支援（毎月2千円）',
        '求職活動やオンライン学習に必要な通信費の一部を補助する制度です。対象期間中の利用証明提出が必要です。'
    ),
    createSupport(
        '31005',
        '通勤定期購入補助（上限月1万円）',
        '就労開始初期の負担軽減を目的に、通勤定期代を補助する制度です。勤務先証明と定期購入証明が必要です。'
    ),
    createSupport(
        '31006',
        'メンタルヘルス相談費補助（年額1.5万円）',
        '就労継続支援の一環として、カウンセリング利用費を補助する制度です。指定相談機関の利用に限り対象です。'
    ),
    createSupport(
        '31007',
        '学び直し教材費補助（上限2万円）',
        '再就職に必要な基礎学習や資格学習の教材購入費を補助する制度です。対象教材と領収書提出が必要です。'
    ),
    createSupport(
        '31008',
        '面接交通費助成（1回2千円・上限10回）',
        '求職面接にかかる交通費を回数上限付きで助成する制度です。面接日時と企業訪問記録の提出が必要です。'
    ),
    createSupport(
        '31009',
        '子ども預かり利用補助（上限月1.5万円）',
        '就職活動や勤務中に必要な一時預かり費用を補助する制度です。利用明細と勤務実績の確認があります。'
    ),
    createSupport(
        '31010',
        '自立支援住宅初期費用補助（上限8万円）',
        '安定就労に向けた住居確保のため、敷金礼金など初期費用の一部を補助する制度です。契約内容審査後に支給されます。'
    ),
    createSupport(
        '31011',
        '就労継続応援給付（6か月経過時3万円）',
        '就労開始後6か月継続した対象者へ、定着支援として給付を行う制度です。勤務証明と所得確認が必要です。'
    ),
    createSupport(
        '31012',
        '家計相談伴走支援（無料・予約制）',
        '収支改善と制度活用を支援する家計相談を継続提供する制度です。相談結果に基づき追加支援の案内を受けられます。'
    ),
];

export const CITIZEN_SUPPORT_BY_CARD_UID = {
    'AB12CD34EF56': {
        name: '山田花子',
        age: 32,
        gender: 'woman',
        can_use_support: SUPPORTS_FOR_HANAKO,
    },
    'F091AA33BC7D': {
        name: '佐藤太郎',
        age: 68,
        gender: 'man',
        can_use_support: SUPPORTS_FOR_TARO,
    },
    '7733EE19A4C2': {
        name: '鈴木一郎',
        age: 44,
        gender: 'man',
        can_use_support: SUPPORTS_FOR_ICHIRO,
    },
};

export const getCitizenSupportByCardUid = (cardUid) => {
    if (!cardUid) {
        return null;
    }
    return CITIZEN_SUPPORT_BY_CARD_UID[cardUid] || null;
};
